import React, { useEffect, useState } from "react";
import Carousel from "react-multi-carousel";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Cell,
  Legend,
  PieChart,
  Pie,
  Rectangle,
  AreaChart,
  Area,
} from "recharts";
import { GET_TOKEN, GLOBAL_API } from "../../api/apiUrls";
import { responsive } from "../../static/csstemp";
import { packageCodes } from "../../static/dropdown";

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="black"
      textAnchor="middle"
      dominantBaseline="central"
      fontWeight="bold"
      fontSize={"10px"}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const Dashboard = ({ reportQuery, columnData, packageReports }) => {
  const [paymentBarData, setPaymentBarData] = useState([]);
  const [paymentBarDataLocWise, setPaymentBarDataLocWise] = useState([]);
  const [visaBarDataLocWise, setVisaBarDataLocWise] = useState([]);
  const [accoBarDataLocWise, setAccoBarDataLocWise] = useState([]);
  const [regAmtPenBarDataLocWise, setRegAmtPenBarDataLocWise] = useState([]);
  const [studentsBranchWise, setStudentBranchWise] = useState([]);
  const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042"];

  const fetchBaseQuery = async (query) => {
    try {
      const res = await axios.post(GLOBAL_API, {
        mstr: query,
        tokenNumber: GET_TOKEN,
      });
      return res;
    } catch (err) {
      console.error("Error fetching count:", err);
      return null;
    }
  };

  const fetchPaymentBarChartData = async () => {
    try {
      const finalData = [];
      const distinctStatusRes = await fetchBaseQuery(
        "SELECT DISTINCT PaymentStatus FROM StudentMas WHERE PaymentStatus NOT IN ('Drop', 'Hold')"
      );
      const totalStudent = await fetchBaseQuery(
        `SELECT COUNT(*) FROM StudentMas WHERE ststatus IN (0, 3, 5, 6, 7)`
      );

      const studentCount = totalStudent?.data[0]?.Column1 || 0;
      // console.log("distinct statuses:", distinctStatusRes);

      if (!distinctStatusRes?.data?.length) return [];

      const statusDataPromises = distinctStatusRes.data.map(async (item) => {
        const queryRes = await fetchBaseQuery(
          `${reportQuery?.totalAssignedUser?.query}  AND PaymentStatus = '${item.PAYMENTSTATUS}'`
        );
        return {
          totalStudent: studentCount,
          name: item.PAYMENTSTATUS,
          count: queryRes?.data?.[0]?.Column1 || 0,
        };
      });

      const resolvedData = await Promise.all(statusDataPromises);
      setPaymentBarData(resolvedData);
      return resolvedData;
    } catch (error) {
      console.error("Error fetching payment bar chart data:", error);
      return [];
    }
  };

  const fetchPaymentBarChartDataLocationWise = async () => {
    try {
      const distinctStatusRes = await fetchBaseQuery(
        "SELECT LocCode, locname FROM locmas WHERE LocCode NOT IN (15, 16, 17, 18, 19)"
      );

      if (!distinctStatusRes?.data?.length) return [];

      if (!reportQuery) {
        return {
          name: "NA",
          count: 0,
        };
      }
      const statusDataPromises = distinctStatusRes.data.map(async (item) => {
        const queryRes = await fetchBaseQuery(
          `${reportQuery?.payment_pending?.query} AND st.LocCode=${item.LOCCODE}`
        );
        const queryRes2 = await fetchBaseQuery(
          `${reportQuery?.total_admission_given?.query} AND st.LocCode=${item.LOCCODE}`
        );

        return {
          name: item.LOCNAME,
          pending: queryRes?.data?.[0]?.Column1 || 0,
          total: queryRes2?.data?.[0]?.Column1 || 0,
        };
      });

      const resolvedData = await Promise.all(statusDataPromises);
      setPaymentBarDataLocWise(resolvedData);
      //   return resolvedData;
    } catch (error) {
      console.error("Error fetching payment bar chart data:", error);
      return [];
    }
  };

  const fetchVisaBarChartDataLocationWise = async () => {
    try {
      const distinctStatusRes = await fetchBaseQuery(
        "SELECT LocCode, locname FROM locmas WHERE LocCode NOT IN (15, 16, 17, 18, 19)"
      );

      if (!distinctStatusRes?.data?.length) return [];

      if (!reportQuery) {
        return {
          name: "NA",
          count: 0,
        };
      }
      const statusDataPromises = distinctStatusRes.data.map(async (item) => {
        const queryRes = await fetchBaseQuery(
          `${reportQuery?.visa_amount_pending?.query} AND st.LocCode=${item.LOCCODE}`
        );

        // console.log("first", queryRes);
        return {
          name: item.LOCNAME,
          count: queryRes?.data?.[0]?.Column1 || 0,
        };
      });

      const resolvedData = await Promise.all(statusDataPromises);
      setVisaBarDataLocWise(resolvedData);
      //   return resolvedData;
    } catch (error) {
      console.error("Error fetching payment bar chart data:", error);
      return [];
    }
  };

  const fetchAccoBarChartDataLocationWise = async () => {
    try {
      const distinctStatusRes = await fetchBaseQuery(
        "SELECT LocCode, locname FROM locmas WHERE LocCode NOT IN (15, 16, 17, 18, 19) "
      );

      if (!distinctStatusRes?.data?.length) return [];

      if (!reportQuery) {
        return {
          name: "NA",
          count: 0,
        };
      }
      const statusDataPromises = distinctStatusRes.data.map(async (item) => {
        const queryRes = await fetchBaseQuery(
          `${reportQuery?.accomodation_amount_pending?.query} AND st.LocCode=${item.LOCCODE}`
        );

        return {
          name: item.LOCNAME,
          count: queryRes?.data?.[0]?.Column1 || 0,
        };
      });

      const resolvedData = await Promise.all(statusDataPromises);
      setAccoBarDataLocWise(resolvedData);
    } catch (error) {
      console.error("Error fetching payment bar chart data:", error);
      return [];
    }
  };

  const fetchRegAmtBarChartDataLocationWise = async () => {
    try {
      const distinctStatusRes = await fetchBaseQuery(
        "SELECT LocCode, locname FROM locmas WHERE LocCode NOT IN (15, 16, 17, 18, 19)"
      );

      if (!distinctStatusRes?.data?.length) return [];

      if (!reportQuery) {
        return {
          name: "NA",
          count: 0,
        };
      }
      const statusDataPromises = distinctStatusRes.data.map(async (item) => {
        const queryRes = await fetchBaseQuery(
          `${reportQuery?.registration_amount_pending?.query} AND st.LocCode=${item.LOCCODE}`
        );

        // console.log(
        //   `${reportQuery?.registration_amount_pending?.query} AND st.LocCode=${item.LOCCODE}`
        // );

        return {
          name: item.LOCNAME,
          count: queryRes?.data?.[0]?.Column1 || 0,
        };
      });

      const resolvedData = await Promise.all(statusDataPromises);
      setRegAmtPenBarDataLocWise(resolvedData);
      //   return resolvedData;
    } catch (error) {
      console.error("Error fetching payment bar chart data:", error);
      return [];
    }
  };

  const fetchStudentBarChartDataLocationWise = async () => {
    try {
      const locationQuery = `
        SELECT LocCode, LocName 
        FROM LocMas 
        WHERE LocCode NOT IN (15, 16, 17, 18, 19)
      `;
      const distinctStatusRes = await fetchBaseQuery(locationQuery);

      const locations = distinctStatusRes?.data || [];
      if (!locations.length || !reportQuery) {
        return [{ name: "NA", count: 0 }];
      }

      const statusDataPromises = locations.map(async (loc) => {
        const { LOCCODE, LOCNAME } = loc;

        const [totalRes, holdRes, dropRes] = await Promise.all([
          fetchBaseQuery(
            `SELECT COUNT(StdCode) AS 'Total' FROM StudentMas WHERE LocCode = ${LOCCODE} AND ststatus IN (0, 3, 5, 6, 7)`
          ),
          fetchBaseQuery(
            `SELECT COUNT(StdCode) AS 'Hold' FROM StudentMas WHERE StStatus IN (1, 4, 9, 10, 11, 12) AND LocCode = ${LOCCODE}`
          ),
          fetchBaseQuery(
            `SELECT COUNT(StdCode) AS 'Drop' FROM StudentMas WHERE ststatus = 2 AND LocCode = ${LOCCODE}`
          ),
        ]);

        return {
          name: LOCNAME,
          count: totalRes?.data[0]?.TOTAL || 0,
          hold: holdRes?.data[0].HOLD,
          drop: dropRes?.data[0].DROP,
        };
      });

      const resolvedData = await Promise.all(statusDataPromises);
      setStudentBranchWise(resolvedData);
      return resolvedData;
    } catch (error) {
      console.error(
        "Error fetching student bar chart data location-wise:",
        error
      );
      return [];
    }
  };

  useEffect(() => {
    fetchPaymentBarChartData();
    fetchPaymentBarChartDataLocationWise();
    fetchVisaBarChartDataLocationWise();
    fetchAccoBarChartDataLocationWise();
    fetchRegAmtBarChartDataLocationWise();
    fetchStudentBarChartDataLocationWise();
  }, [reportQuery]);

  return (
    <div>
      <div className="row bg-light py-3">
        {/* <div className="col-lg-12">
          <p className="h5 fw-bold text-warning">Student with Payment Status</p>
          <div style={{ width: "100%", aspectRatio: "16 / 9" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={paymentBarData}
                margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
              >
                <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                <XAxis
                  dataKey="name"
                  type="category"
                  stroke="#8884d8"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                />
                <YAxis type="number" />
                <Tooltip
                  formatter={(value, name) =>
                    name === "count"
                      ? [`${value}`, "Status Count"]
                      : [`${value}`, name]
                  }
                  labelFormatter={(label) => `Status: ${label}`}
                />
                <Bar dataKey="count" fill="#8884d8" barSize={80}>
                  <LabelList dataKey="count" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div> */}
        <Carousel
          ssr={true} // means to render carousel on server-side.
          infinite={true}
          containerClass="carousel-container"
          removeArrowOnDeviceType={["tablet", "mobile"]}
          responsive={responsive}
        >
          {/* <div className="col-lg">
            <p className="h5 fw-bold text-warning">Admission Amount Pending</p>
            <div style={{ width: "100%", aspectRatio: "16 / 9" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={paymentBarDataLocWise}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: "#333",
                      fontSize: 12,
                      fontWeight: 500,
                      dy: 20,
                    }}
                    angle={45}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="pending" stackId="a" fill="#8884d8" />

                  <Bar dataKey="total" stackId="a" fill="#82ca9d">
                    <LabelList
                      dataKey="total"
                      position="top"
                      valueAccessor={(entry) => entry.pending + entry.total}
                      content={({ x, y, value }) => (
                        <text
                          x={x}
                          y={y - 5}
                          fill="#000"
                          fontWeight="bold"
                          textAnchor="middle"
                        >
                          {value}
                        </text>
                      )}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div> */}

          <div className="col-lg">
            <p className="h5 fw-bold text-warning">Admission Amount Pending</p>
            <div style={{ width: "100%", aspectRatio: "16 / 9" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={paymentBarDataLocWise}
                  margin={{ top: 20, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  {/* Show location names on X-Axis */}
                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: "#333",
                      fontSize: 12,
                      fontWeight: 500,
                      dy: 20,
                    }}
                    angle={45}
                    interval={0}
                  />

                  <YAxis />
                  <Tooltip formatter={(value) => [`${value}`, "pending"]} />

                  <Bar dataKey="pending" fill="#8884d8" radius={[8, 8, 0, 0]}>
                    {/* Count at the top of each bar */}
                    <LabelList
                      dataKey="pending"
                      position="top"
                      style={{ fill: "#333", fontWeight: "bold" }}
                    />

                    {paymentBarDataLocWise.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill="#8884d8" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="col-lg">
            <p className="h5 fw-bold text-warning">Visa Amount Pending</p>
            <div style={{ width: "100%", aspectRatio: "16 / 9" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={visaBarDataLocWise}
                  margin={{ top: 20, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  {/* Show location names on X-Axis */}
                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: "#333",
                      fontSize: 12,
                      fontWeight: 500,
                      dy: 20,
                    }}
                    angle={45}
                    interval={0}
                  />

                  <YAxis />
                  <Tooltip formatter={(value) => [`${value}`, "count"]} />

                  <Bar dataKey="count" fill="#8884d8" radius={[8, 8, 0, 0]}>
                    {/* Count at the top of each bar */}
                    <LabelList
                      dataKey="count"
                      position="top"
                      style={{ fill: "#333", fontWeight: "bold" }}
                    />

                    {visaBarDataLocWise.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill="#8884d8" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="col-lg">
            <p className="h5 fw-bold text-warning">
              Accomodation Amount Pending
            </p>
            <div style={{ width: "100%", aspectRatio: "16 / 9" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={accoBarDataLocWise}
                  margin={{ top: 20, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  {/* Show location names on X-Axis */}
                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: "#333",
                      fontSize: 12,
                      fontWeight: 500,
                      dy: 20,
                    }}
                    angle={45}
                    interval={0}
                  />

                  <YAxis />
                  <Tooltip formatter={(value) => [`${value}`, "count"]} />

                  <Bar dataKey="count" fill="#8884d8" radius={[8, 8, 0, 0]}>
                    {/* Count at the top of each bar */}
                    <LabelList
                      dataKey="count"
                      position="top"
                      style={{ fill: "#333", fontWeight: "bold" }}
                    />

                    {accoBarDataLocWise.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill="#8884d8" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Carousel>
      </div>

      <div className="row bg-light">
        <div className="col-6">
          <p className="h5 fw-bold text-warning">Student with Payment Status</p>
          <div style={{ width: "100%", height: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={paymentBarData}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  innerRadius={85} // 👈 this makes it a donut
                  labelLine={false}
                  label={renderCustomizedLabel}
                >
                  {paymentBarData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend layout="vertical" verticalAlign="center" align="left" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="col-6">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              width={500}
              height={300}
              data={studentsBranchWise}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend verticalAlign="top" align="center" />
              <Bar
                dataKey="count"
                fill="#23f1f20"
                activeBar={<Rectangle fill="pink" stroke="#23f1f20" />}
              />
              <Bar
                dataKey="drop"
                fill="#cb372b"
                activeBar={<Rectangle fill="gold" stroke="#cb372b" />}
              />
              <Bar
                dataKey="hold"
                fill="#ffd300"
                activeBar={<Rectangle fill="gold" stroke="#ffd300" />}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="card border-warning overflow-hidden rounded">
            <div className="card-body p-0 m-0 overflow-auto wb-scrollbar-w-1 max-h-60">
              <table className="table">
                <thead>
                  <tr className="sticky-top">
                    <th className="text-xs sticky-left">Branch</th>
                    <th className="text-xs">Admission Pending</th>
                    <th className="text-xs">Visa Pending</th>
                    <th className="text-xs">Accomodation Pending</th>
                    <th className="text-xs">Registration Pending</th>
                  </tr>
                  {paymentBarDataLocWise?.map((item, idx) => (
                    <tr key={idx}>
                      <th className="text-xs sticky-left" >{item?.name}</th>
                      <td className="text-xs">{item?.pending}</td>
                      <td className="text-xs">
                        {
                          visaBarDataLocWise.find((area) => {
                            return area.name === item.name;
                          }).count
                        }
                      </td>
                      <td className="text-xs">
                        {
                          accoBarDataLocWise.find((area) => {
                            return area.name === item.name;
                          }).count
                        }
                      </td>
                      <td className="text-xs">
                        {
                          regAmtPenBarDataLocWise.find((area) => {
                            return area.name === item.name;
                          }).count
                        }
                      </td>
                    </tr>
                  ))}
                </thead>
              </table>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card border-warning overflow-hidden rounded">
            <div className="card-body p-0 m-0 overflow-auto max-h-60 wb-scrollbar-w-1">
              <table className="table table-hover">
                <thead>
                  <tr className="sticky-top">
                    <th>Metric</th>
                    {packageCodes.map((pkg) => (
                      <th key={pkg.value} className="text-xs" width="1%">
                        {pkg.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {columnData.map((col, idx) => (
                    <tr key={idx}>
                      <td className="text-xs fw-bold sticky-left">{col.label}</td>
                      {packageReports.map((pkg, i) => (
                        <td key={i} className="text-xs">
                          {pkg[col.value] !== undefined ? pkg[col.value] : "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
